if (!customElements.get('product-details')) {
  customElements.define(
    'product-details',
    class ProductDetails extends HTMLElement {
      connectedCallback() {
        this.variantData = this.getVariantData();
        this.optionGroups = Array.from(this.querySelectorAll('[data-option-position]'));
        this.masterInput = this.querySelector('[data-variant-id]');
        this.price = this.querySelector('[data-current-price]');
        this.comparePrice = this.querySelector('[data-compare-price]');
        this.availability = this.querySelector('[data-product-availability]');
        this.addButton = this.querySelector('[data-add-to-cart]');
        this.buyButton = this.querySelector('[data-buy-now]');
        this.addButtonText = this.querySelector('[data-add-to-cart-text]');
        this.gallery = this.querySelector('[data-product-gallery]');
        this.galleryItems = Array.from(this.querySelectorAll('[data-media-id]'));
        this.counter = this.querySelector('[data-gallery-current]');
        this.infoPanel = this.querySelector('.product-page__info-inner');
        this.infoPanelContent = this.querySelector('.product-page__form');
        this.infoDetails = Array.from(this.querySelectorAll('details[name^="ProductInformation-"]'));

        this.onOptionChange = this.onOptionChange.bind(this);
        this.onGalleryScroll = this.onGalleryScroll.bind(this);
        this.onPanelToggle = this.onPanelToggle.bind(this);
        this.schedulePanelOverflowCheck = this.schedulePanelOverflowCheck.bind(this);

        this.optionGroups.forEach((group) => group.addEventListener('change', this.onOptionChange));
        this.infoDetails.forEach((details) => details.addEventListener('toggle', this.onPanelToggle));
        window.addEventListener('resize', this.schedulePanelOverflowCheck);
        window.addEventListener('orientationchange', this.schedulePanelOverflowCheck);
        if (this.gallery && this.counter) {
          this.gallery.addEventListener('scroll', this.onGalleryScroll, { passive: true });
        }

        if ('ResizeObserver' in window && this.infoPanelContent) {
          this.infoPanelResizeObserver = new ResizeObserver(this.schedulePanelOverflowCheck);
          this.infoPanelResizeObserver.observe(this.infoPanelContent);
        }

        this.updateOptionAvailability();
        this.showInitialFeaturedMedia();
        this.schedulePanelOverflowCheck();
      }

      disconnectedCallback() {
        this.optionGroups.forEach((group) => group.removeEventListener('change', this.onOptionChange));
        this.infoDetails.forEach((details) => details.removeEventListener('toggle', this.onPanelToggle));
        window.removeEventListener('resize', this.schedulePanelOverflowCheck);
        window.removeEventListener('orientationchange', this.schedulePanelOverflowCheck);
        this.infoPanelResizeObserver?.disconnect();
        if (this.panelOverflowFrame) cancelAnimationFrame(this.panelOverflowFrame);
        if (this.gallery && this.counter) {
          this.gallery.removeEventListener('scroll', this.onGalleryScroll);
        }
      }

      getVariantData() {
        const data = this.querySelector('[data-product-variants]');
        if (!data) return [];

        try {
          return JSON.parse(data.textContent);
        } catch (error) {
          return [];
        }
      }

      getSelectedOptions() {
        return this.optionGroups.map((group) => group.querySelector('[data-option-value]:checked')?.value);
      }

      onOptionChange(event) {
        const changedPosition = Number(event.currentTarget.dataset.optionPosition);

        this.updateOptionAvailability();
        this.selectAvailableDownstreamOptions(changedPosition + 1);
        this.updateOptionAvailability();

        const selectedOptions = this.getSelectedOptions();
        const variant = this.variantData.find((candidate) =>
          candidate.options.every((option, index) => option === selectedOptions[index])
        );

        this.updateVariant(variant);
      }

      updateOptionAvailability() {
        const selectedOptions = this.getSelectedOptions();

        this.optionGroups.forEach((group, position) => {
          group.querySelectorAll('[data-option-value]').forEach((input) => {
            const available = this.variantData.some((variant) => {
              if (!variant.available || variant.options[position] !== input.value) return false;

              return variant.options
                .slice(0, position)
                .every((option, index) => option === selectedOptions[index]);
            });

            input.disabled = !available;
            input.setAttribute('aria-label', available ? input.value : `${input.value} — Épuisé`);
          });
        });
      }

      selectAvailableDownstreamOptions(startPosition) {
        this.optionGroups.slice(startPosition).forEach((group) => {
          const checked = group.querySelector('[data-option-value]:checked');
          if (checked && !checked.disabled) return;

          const firstAvailable = group.querySelector('[data-option-value]:not(:disabled)');
          if (firstAvailable) firstAvailable.checked = true;
          this.updateOptionAvailability();
        });
      }

      updateVariant(variant) {
        const available = Boolean(variant?.available);

        if (this.masterInput) {
          this.masterInput.value = variant?.id || '';
          this.masterInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (this.price) this.price.textContent = variant?.price || '';
        if (this.comparePrice) {
          this.comparePrice.textContent = variant?.compareAtPrice || '';
          this.comparePrice.hidden = !variant?.onSale;
        }

        if (this.availability) this.availability.textContent = available ? 'Disponible' : 'Épuisé';
        const busy = this.infoPanelContent?.dataset.cartBusy === 'true';
        if (this.addButton) this.addButton.disabled = busy || !available;
        if (this.buyButton) this.buyButton.disabled = busy || !available;
        if (this.addButtonText) {
          this.addButtonText.textContent = available ? this.dataset.addLabel : this.dataset.soldOutLabel;
        }

        if (variant?.id) this.updateUrl(variant.id);
        if (variant?.featuredMediaId) this.showFeaturedMedia(variant.featuredMediaId);
        this.schedulePanelOverflowCheck();
      }

      schedulePanelOverflowCheck() {
        if (this.panelOverflowFrame) cancelAnimationFrame(this.panelOverflowFrame);
        this.panelOverflowFrame = requestAnimationFrame(() => {
          this.panelOverflowFrame = requestAnimationFrame(() => this.updatePanelOverflow());
        });
      }

      onPanelToggle(event) {
        if (!event.currentTarget.open && this.infoPanel) this.infoPanel.scrollTop = 0;
        this.schedulePanelOverflowCheck();
      }

      updatePanelOverflow() {
        if (!this.infoPanel) return;

        const usesStickyPanel = window.matchMedia(
          '(min-width: 1025px), (min-width: 769px) and (orientation: landscape)'
        ).matches;

        if (!usesStickyPanel) {
          this.infoPanel.classList.remove('is-overflowing');
          this.infoPanel.scrollTop = 0;
          return;
        }

        this.infoPanel.classList.remove('is-overflowing');
        const isOverflowing = this.infoPanel.scrollHeight > this.infoPanel.clientHeight + 2;
        this.infoPanel.classList.toggle('is-overflowing', isOverflowing);

        if (!isOverflowing) this.infoPanel.scrollTop = 0;
      }

      updateUrl(variantId) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }

      showInitialFeaturedMedia() {
        if (!window.matchMedia('(max-width: 768px)').matches) return;

        const variant = this.variantData.find((candidate) => String(candidate.id) === this.masterInput?.value);
        const item = this.galleryItems.find(
          (media) => String(media.dataset.mediaId) === String(variant?.featuredMediaId)
        );

        if (!item || !this.gallery) return;
        this.gallery.scrollLeft = item.offsetLeft;
        if (this.counter) this.counter.textContent = String(this.galleryItems.indexOf(item) + 1);
      }

      showFeaturedMedia(mediaId) {
        const item = this.galleryItems.find((media) => String(media.dataset.mediaId) === String(mediaId));
        if (!item) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        item.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
      }

      onGalleryScroll() {
        if (this.galleryFrame) cancelAnimationFrame(this.galleryFrame);
        this.galleryFrame = requestAnimationFrame(() => {
          const galleryLeft = this.gallery.getBoundingClientRect().left;
          let closestIndex = 0;
          let closestDistance = Infinity;

          this.galleryItems.forEach((item, index) => {
            const distance = Math.abs(item.getBoundingClientRect().left - galleryLeft);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestIndex = index;
            }
          });

          this.counter.textContent = String(closestIndex + 1);
        });
      }
    }
  );
}
